import React, { useContext, useEffect, useRef, useState } from "react";
import Header from "../components/Header/Header";
import MobileNavbar from "../components/Header/Mobile/MobileNavbar";
import MobileHeader from "../components/Header/Mobile/MobileHeader";
import {
  MediaCommunitySkin,
  MediaOutlet,
  MediaPlayer,
  MediaPoster,
} from "@vidstack/react";
import { toFarsiNumber } from "../helper/helper";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { getData, postData } from "../services/API";
import Skeleton from "react-loading-skeleton";
import AcademyAccordion from "../components/Academy/AcademyAccordion";
import EpisodePageAccordion from "../components/Academy/EpisodePageAccordion";
import logo from "../images/header-logo.png";
import { UserData } from "../contexts/UserInfoContext";
import { getItemStorage } from "../Localstorage/useLocalStorage";
import Footer from "../components/Footer/Footer";
import Loading from "../components/shared/Loading";
import EpisodeQs from "../components/EpisodePage/EpisodeQs";
import SendComment from "../components/EpisodePage/SendComment";
import Input from "../components/shared/Form/Input";
import CircleLoading from "../components/shared/CircleLoading";
import { Toast } from "primereact/toast";
import { useForm } from "react-hook-form";
import EpisodeQsTab from "../components/EpisodePage/EpisodeQsTab";
import HLSPlayer from "../components/player/HLSPlayer";
import AudioTrackSelector from "../components/player/AudioTrackSelector";
import AudioSync from "../components/player/AudioSync";

const EpisodePage = () => {
  const { getValues, register, setValue } = useForm();
  const { user, userLoaded } = useContext(UserData);
  const [episodeData, setEpisodeData] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [percent, setPercent] = useState("0%");
  const [subtitles, setSubtitles] = useState([]);
  const [lessonData, setLessonData] = useState({});
  const [data, setData] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [nextEp, setNextEp] = useState("");
  const [qa, setQa] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);
  const [saveNoteLoading, setSaveNoteLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioSyncOffset, setAudioSyncOffset] = useState(0);

  const toast = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const navigate = useNavigate();

  const param = useParams("slug");

  const token = getItemStorage("token");

  const loc = useLocation();

  useEffect(() => {
    setLoading(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    if (userLoaded) {
      getData(`/acd/episode/show/${param.slug}`, {})
        .then((res) => {
          setLoading(false);
          setData(res?.data);
          setLessonData(res?.data?.lesson);
          setSubtitles(
            res?.data?.subtitles?.map((item) => ({
              ...item,
              src: `https://${window.location.hostname}/academy/subtitles/${item.subtitle}`,
            }))
          );

          setEpisodeData(res?.data?.episode);
          setUnits(res?.data?.lesson.units);
          setPercent(res?.data?.percent);
          setValue("note", res?.data?.note);
          setQuestions(res?.data?.questions);
          setAudioUrl(res?.data?.episode?.audio_url || "");
          setAudioSyncOffset(res?.data?.episode?.audio_sync_offset || 0);
        })
        .catch((err) => {});
    }
  }, [loc, userLoaded]);

  // Sync audio with video
  useEffect(() => {
    if (audioUrl && videoRef.current && audioRef.current) {
      const video = videoRef.current.querySelector("video");
      const audio = audioRef.current;

      if (video && audio) {
        const syncAudio = () => {
          if (!video.paused) {
            const targetTime = video.currentTime + audioSyncOffset;
            if (Math.abs(audio.currentTime - targetTime) > 0.1) {
              audio.currentTime = Math.max(0, targetTime);
            }
            if (audio.paused) {
              audio.play().catch(() => {});
            }
          } else {
            if (!audio.paused) {
              audio.pause();
            }
          }
        };

        const handlePlay = () => {
          audio.currentTime = Math.max(0, video.currentTime + audioSyncOffset);
          audio.play().catch(() => {});
        };

        const handlePause = () => {
          audio.pause();
        };

        const handleSeeked = () => {
          audio.currentTime = Math.max(0, video.currentTime + audioSyncOffset);
        };

        const handleTimeUpdate = () => {
          syncAudio();
        };

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("timeupdate", handleTimeUpdate);

        // Initial sync
        syncAudio();

        return () => {
          video.removeEventListener("play", handlePlay);
          video.removeEventListener("pause", handlePause);
          video.removeEventListener("seeked", handleSeeked);
          video.removeEventListener("timeupdate", handleTimeUpdate);
        };
      }
    }
  }, [audioUrl, audioSyncOffset]);

  const finishedVideoHandler = () => {
    postData("/acd/episode/watched-episode", { lesson_id: episodeData.id })
      .then((res) => {
        setOpenModal(true);

        setNextEp(res.data.next);
      })
      .catch((err) => {});
  };

  const nextEpisodeHandler = () => {
    setOpenModal(false);
    navigate(`/ep/${nextEp.id}`);
  };

  const saveNoteHandler = () => {
    setSaveNoteLoading(true);

    postData("/acd/lesson/note-save", {
      lesson_id: lessonData.id,
      ...getValues(),
    })
      .then((res) => {
        toast.current.show({
          severity: "success",
          summary: "با موفقیت انجام شد",
          detail: "یادداشت شما با موفقیت ذخیره شد",
          life: 3500,
        });

        setSaveNoteLoading(false);
      })
      .catch((err) => {
        setSaveNoteLoading(false);

        toast.current.show({
          severity: "error",
          summary: "خطا هنگام ذخیره یادداشت",
          detail: "لطفا دقایقی دیگر تلاش کنید",
          life: 3500,
        });
      });
  };

  return (
    <div className="blog flex flex-col items-center lg:gap-10 justify-center">
      <Header />
      <MobileNavbar />
      <MobileHeader />
      <Toast ref={toast} />

      {/* ask for next episode */}
      <div
        className={`${
          openModal ? "opacity-100 visible" : "opacity-0 invisible"
        } transition-all duration-300 w-full h-full fixed bg-black bg-opacity-60 right-0 z-[99999999] top-0`}
      ></div>

      <div
        className={`${
          openModal
            ? "opacity-100 visible scale-100"
            : "opacity-0 invisible scale-90"
        } transition-all duration-300 max-w-96 w-full h-fit rounded-2xl p-4 fixed bg-[#222] -translate-y-2/4  z-[999999909] -translate-x-2/4 top-2/4 left-2/4`}
      >
        <div className="flex flex-col items-center text-gray-200 text-lg gap-5">
          <div className="w-20 h-20 rounded-full bg-gradient-to-tl text-white from-green-600 to-green-300 shadow-lg shadow-green-900 flex justify-center items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="size-12"
            >
              <path d="M4.5 4.5a3 3 0 0 0-3 3v9a3 3 0 0 0 3 3h8.25a3 3 0 0 0 3-3v-9a3 3 0 0 0-3-3H4.5ZM19.94 18.75l-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06Z" />
            </svg>
          </div>

          <p>
            تبریک! شما این قسمت از دوره را به طور کامل مشاهده کردید مایلید به
            قسمت بعد بروید ؟
          </p>

          <div className="flex items-center gap-4">
            <button
              onClick={nextEpisodeHandler}
              className="rounded-xl py-2 px-4 flex justify-center items-center text-sm bg-gradient-to-tl text-white from-green-600 to-green-400"
            >
              قسمت بعد
            </button>

            <button
              onClick={() => setOpenModal(false)}
              className="rounded-xl py-2 px-4 text-sm bg-gradient-to-tl text-white from-red-600 to-red-400"
            >
              لغو
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="w-full max-w-[1300px] h-auto flex flex-col pb-20 gap-4 lg:gap-7 px-3 2xl:px-0">
          <div className="w-full rounded-xl overflow-hidden relative">
            <Skeleton className="block w-full md:h-[720px] h-64 rounded-xl" />
          </div>

          <section className="relative flex flex-col-reverse xl:grid grid-cols-1 xl:grid-cols-10 gap-4">
            {/* right side */}
            <div className="xl:col-span-7 flex flex-col gap-4">
              <div className="w-full h-auto flex flex-col gap-5 bg-[#222] rounded-xl p-5">
                <Skeleton className="block w-52 h-14 rounded-xl -mb-4" />

                <div className="flex flex-col gap-4">
                  <Skeleton
                    count={10}
                    className="w-full block h-5 rounded-lg -mb-2"
                  />
                </div>
              </div>
            </div>

            {/* sidebar */}
            <div className="xl:col-span-3 block w-full xl:sticky top-4">
              <div className="flex flex-col gap-4">
                <div className="w-full h-auto bg-[#222] rounded-xl p-5 flex flex-col gap-4">
                  <div className="w-full flex items-center self-start gap-2 pb-4 border-b border-gray-800">
                    <span className="block w-5 h-2 bg-yellow-700 rounded-full"></span>
                    <h2 className="text-gray-200 text-2xl font-black">
                      سرفصل های دوره
                    </h2>
                  </div>

                  <div className="flex flex-col gap-4">
                    <Skeleton
                      count={4}
                      className="block w-full h-12 rounded-xl -mb-2"
                    />
                  </div>
                </div>

                <div className="w-full bg-[#222] rounded-xl h-auto p-5 flex flex-col gap-3">
                  <Skeleton className="block w-full h-6 rounded-lg" />
                  <Skeleton className="block w-full h-6 rounded-lg -mt-6" />
                  <Skeleton className="block w-full h-7 rounded-lg -mt-3 -mb-6" />
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : episodeData.lock === 0 ||
        data.buy === false ||
        user?.user?.developer === 1 ||
        lessonData?.ranks?.filter(
          (item) => item.rank >= user?.user?.level || item.rank === 100
        ).length ? (
        <div className="w-full max-w-[1300px] h-auto flex flex-col pb-20 gap-4 lg:gap-7 px-3 2xl:px-0">
          <div className="w-full rounded-xl overflow-hidden relative">
            {loading ? (
              <Skeleton className="block w-full md:h-[720px] h-64 rounded-xl" />
            ) : (
              <HLSPlayer
                poster={`https://api.${window.location.hostname}/${episodeData.image}`}
                src={`https://${window.location.hostname}/academy/videos/${episodeData.video?.video}.m3u8`}
                title={episodeData?.name}
                onEnd={finishedVideoHandler}
                subtitles={subtitles}
              />

              // <div className="relative w-full">
              //   <MediaPlayer
              //     ref={videoRef}
              //     poster={`https://api.${window.location.hostname}/${episodeData.image}`}
              //     onEnd={finishedVideoHandler}
              //     src={`https://${window.location.hostname}/academy/videos/${episodeData.video?.video}.m3u8`}
              //     aspectRatio={16 / 9}
              //     className="!rounded-xl !h-full !w-full video-player-episode"
              //   >
              //     <MediaOutlet>
              //       <MediaPoster className="vds-poster" />

              //   <img
              //     src={logo}
              //     className="w-20 absolute left-4 bottom-4"
              //     alt=""
              //   />

              //       {subtitles.map((sub, i) => (
              //         <track
              //           className="sub-test"
              //           src={`https://${window.location.hostname}/academy/subtitles/${sub.subtitle}`}
              //           label={sub.language}
              //           srcLang="fa"
              //           kind="subtitles"
              //           default
              //         />
              //       ))}
              //     </MediaOutlet>
              //     <MediaCommunitySkin />
              //   </MediaPlayer>
              //   {audioUrl && (
              //     <audio
              //       ref={audioRef}
              //       src={audioUrl}
              //       preload="auto"
              //       style={{ display: "none" }}
              //     />
              //   )}
              // </div>
            )}
          </div>

          <section className="relative flex flex-col-reverse xl:grid grid-cols-1 xl:grid-cols-10 gap-4">
            {/* right side */}
            <div className="xl:col-span-7 flex flex-col gap-4">
              <div className="w-full h-auto flex flex-col gap-5 bg-[#222] rounded-xl p-5">
                {/* course name & unit */}
                {loading ? (
                  <Skeleton className="block w-52 h-14 rounded-xl -mb-4" />
                ) : (
                  <div className="flex items-center self-start gap-2">
                    <span className="block w-5 h-2 bg-yellow-700 rounded-full"></span>
                    <h2 className="text-gray-200 text-2xl font-black">
                      {episodeData.name}
                    </h2>
                  </div>
                )}

                {/* description */}
                <div className="flex flex-col gap-4">
                  {loading ? (
                    <Skeleton
                      count={10}
                      className="w-full block h-5 rounded-lg -mb-2"
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: lessonData.description,
                      }}
                      className="course-content leading-8 text-[17px] text-gray-400"
                    ></div>
                  )}
                </div>
              </div>

              <div className="w-full h-auto flex flex-col gap-5 bg-[#222] rounded-xl p-5">
                {/* course name & unit */}
                {loading ? (
                  <Skeleton className="block w-52 h-14 rounded-xl -mb-4" />
                ) : (
                  <div className="flex items-center self-start gap-2">
                    <span className="block w-5 h-2 bg-yellow-700 rounded-full"></span>
                    <h2 className="text-gray-200 text-2xl font-black">
                      یادداشت برداری
                    </h2>
                  </div>
                )}

                {/* description */}
                <div className="flex flex-col gap-4">
                  <Input
                    label={"یادداشت شما"}
                    placeHolder={"یادداشت خود را بنویسید"}
                    register={{ ...register("note") }}
                    className={"!h-44"}
                  />

                  <button
                    onClick={saveNoteHandler}
                    className="w-full h-11 rounded-xl flex justify-center items-center text-white bg-green-600"
                  >
                    {saveNoteLoading ? (
                      <CircleLoading />
                    ) : (
                      <span>ذخیره یادداشت</span>
                    )}
                  </button>
                </div>
              </div>

              {questions.length > 0 && (
                <div className="w-full h-auto flex flex-col gap-5 bg-[#222] rounded-xl p-5">
                  {/* questions */}
                  {loading ? (
                    <Skeleton className="block w-52 h-14 rounded-xl -mb-4" />
                  ) : (
                    <div className="flex items-center self-start gap-2">
                      <span className="block w-5 h-2 bg-yellow-700 rounded-full"></span>
                      <h2 className="text-gray-200 text-2xl font-black">
                        سوالات این درس
                      </h2>
                    </div>
                  )}

                  {/* qustions */}
                  <div className="flex flex-col gap-4">
                    {questions.map((qs, i) => (
                      <EpisodeQsTab
                        data={qs}
                        number={i + 1}
                        setQuestions={setQuestions}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* sidebar */}
            <div className="xl:col-span-3 block w-full xl:sticky top-4">
              <div className="flex flex-col gap-4">
                <div className="w-full h-auto bg-[#222] rounded-xl p-5 flex flex-col gap-4">
                  <div className="w-full flex items-center self-start gap-2 pb-4 border-b border-gray-800">
                    <span className="block w-5 h-2 bg-yellow-700 rounded-full"></span>
                    <h2 className="text-gray-200 text-2xl font-black">
                      سرفصل های دوره
                    </h2>
                  </div>

                  <div className="flex flex-col gap-4">
                    {loading ? (
                      <Skeleton
                        count={4}
                        className="block w-full h-12 rounded-xl -mb-2"
                      />
                    ) : (
                      <div className="flex flex-col gap-4">
                        {units.map((unit) => (
                          <EpisodePageAccordion
                            buy={data.buy}
                            data={unit}
                            active={param.slug}
                            lessonData={lessonData}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-full bg-[#222] rounded-xl h-auto p-5 flex flex-col gap-3">
                  {loading ? (
                    <>
                      <Skeleton className="block w-full h-6 rounded-lg" />
                      <Skeleton className="block w-full h-6 rounded-lg -mt-6" />
                      <Skeleton className="block w-full h-7 rounded-lg -mt-3 -mb-6" />
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-400">
                        وقتی یک ویدیو را کامل بصورت آنلاین تماشا میکنید، میزان
                        پیشرفت شما بصورت خودکار بروزرسانی میشود.
                      </p>

                      <div className="flex items-center mt-3 text-yellow-700 justify-between">
                        <span>درصد تکمیل دوره</span>
                        <span className="text-xl">
                          {toFarsiNumber(percent)}
                        </span>
                      </div>

                      <div className="w-full h-[10px] rounded-full overflow-hidden bg-yellow-800 bg-opacity-20">
                        <div
                          style={{ width: percent }}
                          className="h-full bg-gradient-to-tl rounded-full from-yellow-900 to-yellow-500"
                        ></div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : episodeData.lock === 1 && data.buy ? (
        <div className="w-full bg-[#222] max-w-[1300px] rounded-2xl p-10 flex flex-col gap-5  justify-center items-center">
          <svg
            class="w-80"
            viewBox="0 0 292 272"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="146" cy="142" r="130" fill="#64748B"></circle>
            <path
              d="M82.7 5.2H78.8V1.3C78.8 0.65 78.15 0 77.5 0C76.85 0 76.2 0.65 76.2 1.3V5.2H72.3C71.65 5.2 71 5.85 71 6.5C71 7.15 71.65 7.8 72.3 7.8H76.2V11.7C76.2 12.35 76.85 13 77.5 13C78.15 13 78.8 12.35 78.8 11.7V7.8H82.7C83.35 7.8 84 7.15 84 6.5C84 5.85 83.35 5.2 82.7 5.2Z"
              fill="#FBBF24"
            ></path>
            <path
              d="M144.5 50C146.433 50 148 48.433 148 46.5C148 44.567 146.433 43 144.5 43C142.567 43 141 44.567 141 46.5C141 48.433 142.567 50 144.5 50Z"
              fill="#FBBF24"
            ></path>
            <path
              d="M200.054 270.687C182.924 271 162.056 270.687 147.418 270.373C133.091 270.687 112.223 271 94.7817 270.687C111.912 260.975 117.518 242.804 118.452 230.899H175.76C177.318 242.804 182.924 261.288 200.054 270.687Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M95.0928 270.687C112.223 260.975 117.829 242.804 118.763 230.899H176.071C177.006 239.044 179.809 250.323 187.284 259.721"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M255.492 79.2658V229.019C255.492 232.779 252.377 235.285 249.263 235.285H46.8165C43.3905 235.285 40.5874 232.152 40.5874 229.019V79.2658C40.5874 75.8196 43.702 73 46.8165 73H249.263C253 73 255.492 76.1329 255.492 79.2658Z"
              fill="white"
            ></path>
            <path
              d="M255.492 128.139V229.019C255.492 232.779 252.377 235.285 249.263 235.285H46.8165C43.3905 235.285 40.5874 232.152 40.5874 229.019"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M40.2764 212.101V79.2658C40.2764 75.8196 43.3909 73 46.5055 73H248.952C252.689 73 255.181 76.1329 255.181 79.2658V111.535"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M247.396 81.1455H47.7524V200.823H247.396V81.1455Z"
              fill="white"
            ></path>
            <path
              d="M147.417 222.753C150.22 222.753 152.4 220.56 152.4 217.74C152.4 214.921 149.909 212.728 147.417 212.728C144.925 212.728 142.434 214.921 142.434 217.74C142.434 220.247 144.614 222.753 147.417 222.753Z"
              fill="white"
            ></path>
            <path
              d="M236.183 194.557H205.349C202.857 194.557 200.677 192.364 200.677 189.858V91.7976C200.677 89.2913 202.857 87.0983 205.349 87.0983H236.183C238.674 87.0983 240.855 89.2913 240.855 91.7976V189.858C240.855 192.364 238.986 194.557 236.183 194.557Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M231.198 94.6171H210.642C210.019 94.6171 209.396 93.9905 209.396 93.3639C209.396 92.7373 210.019 92.1107 210.642 92.1107H231.198C231.821 92.1107 232.444 92.7373 232.444 93.3639C232.444 93.9905 231.821 94.6171 231.198 94.6171Z"
              fill="white"
            ></path>
            <path
              d="M234.626 104.956H207.217C206.595 104.956 205.972 104.642 205.972 103.702C205.972 103.076 206.283 102.449 207.217 102.449H234.626C235.249 102.449 235.871 102.763 235.871 103.702C235.871 104.642 235.249 104.956 234.626 104.956Z"
              fill="white"
            ></path>
            <path
              d="M234.626 111.535H207.217C206.595 111.535 205.972 111.222 205.972 110.282C205.972 109.655 206.283 109.029 207.217 109.029H234.626C235.249 109.029 235.871 109.342 235.871 110.282C235.871 111.222 235.249 111.535 234.626 111.535Z"
              fill="white"
            ></path>
            <path
              d="M234.626 117.801H207.217C206.595 117.801 205.972 117.487 205.972 116.547C205.972 115.921 206.283 115.294 207.217 115.294H234.626C235.249 115.294 235.871 115.607 235.871 116.547C235.871 117.487 235.249 117.801 234.626 117.801Z"
              fill="white"
            ></path>
            <path
              d="M234.626 124.38H207.217C206.595 124.38 205.972 124.066 205.972 123.126C205.972 122.5 206.283 121.873 207.217 121.873H234.626C235.249 121.873 235.871 122.187 235.871 123.126C235.871 123.753 235.249 124.38 234.626 124.38Z"
              fill="white"
            ></path>
            <path
              d="M234.626 130.646H207.217C206.595 130.646 205.972 130.332 205.972 129.392C205.972 128.766 206.283 128.139 207.217 128.139H234.626C235.249 128.139 235.871 128.453 235.871 129.392C235.871 130.019 235.249 130.646 234.626 130.646Z"
              fill="#111827"
            ></path>
            <path
              d="M234.626 137.225H207.217C206.595 137.225 205.972 136.911 205.972 135.971C205.972 135.345 206.283 134.718 207.217 134.718H234.626C235.249 134.718 235.871 135.032 235.871 135.971C235.871 136.598 235.249 137.225 234.626 137.225Z"
              fill="#111827"
            ></path>
            <path
              d="M234.626 143.49H207.217C206.595 143.49 205.972 143.177 205.972 142.237C205.972 141.297 206.283 140.984 207.217 140.984H234.626C235.249 140.984 235.871 141.297 235.871 142.237C235.871 142.864 235.249 143.49 234.626 143.49Z"
              fill="#111827"
            ></path>
            <path
              d="M234.626 149.756H207.217C206.595 149.756 205.972 149.443 205.972 148.503C205.972 147.563 206.283 147.25 207.217 147.25H234.626C235.249 147.25 235.871 147.563 235.871 148.503C235.871 149.13 235.249 149.756 234.626 149.756Z"
              fill="#111827"
            ></path>
            <path
              d="M234.626 156.335H207.217C206.595 156.335 205.972 156.022 205.972 155.082C205.972 154.142 206.283 153.829 207.217 153.829H234.626C235.249 153.829 235.871 154.142 235.871 155.082C235.871 155.709 235.249 156.335 234.626 156.335Z"
              fill="#111827"
            ></path>
            <path
              d="M65.1924 149.756H115.648C117.828 149.756 119.697 147.876 119.697 145.683V93.3638C119.697 91.1708 117.828 89.291 115.648 89.291H64.881C62.7008 89.291 60.832 91.1708 60.832 93.3638V145.997C61.1435 147.876 63.0123 149.756 65.1924 149.756Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M90.4219 124.066C97.5853 124.066 103.814 118.114 103.814 110.908C103.814 103.703 97.8968 97.75 90.4219 97.75C83.2584 97.75 77.0293 103.703 77.0293 110.908C77.0293 118.114 82.9469 124.066 90.4219 124.066Z"
              fill="#111827"
            ></path>
            <path
              d="M108.797 133.465H72.6681C71.7338 133.465 71.1108 132.839 71.1108 131.899C71.1108 130.959 71.7338 130.332 72.6681 130.332H108.797C109.731 130.332 110.354 130.959 110.354 131.899C110.043 132.839 109.42 133.465 108.797 133.465Z"
              fill="#111827"
            ></path>
            <path
              d="M97.8955 140.671H83.5685C82.6341 140.671 82.0112 140.044 82.0112 139.104C82.0112 138.165 82.6341 137.538 83.5685 137.538H97.8955C98.8299 137.538 99.4528 138.165 99.4528 139.104C99.4528 139.731 98.8299 140.671 97.8955 140.671Z"
              fill="#111827"
            ></path>
            <path
              d="M133.401 149.756H184.169C186.349 149.756 188.218 147.876 188.218 145.683V93.3638C188.218 91.1708 186.349 89.291 184.169 89.291H133.401C131.221 89.291 129.353 91.1708 129.353 93.3638V145.997C129.664 147.876 131.533 149.756 133.401 149.756Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M158.941 124.066C166.105 124.066 172.334 118.114 172.334 110.908C172.334 103.703 166.416 97.75 158.941 97.75C151.778 97.75 145.549 103.703 145.549 110.908C145.549 118.114 151.778 124.066 158.941 124.066Z"
              fill="white"
            ></path>
            <path
              d="M177.318 133.465H140.878C139.944 133.465 139.321 132.839 139.321 131.899C139.321 130.959 139.944 130.332 140.878 130.332H177.007C177.941 130.332 178.564 130.959 178.564 131.899C178.876 132.839 178.253 133.465 177.318 133.465Z"
              fill="white"
            ></path>
            <path
              d="M166.105 140.671H151.778C150.844 140.671 150.221 140.044 150.221 139.104C150.221 138.165 150.844 137.538 151.778 137.538H166.105C167.039 137.538 167.662 138.165 167.662 139.104C167.974 139.731 167.039 140.671 166.105 140.671Z"
              fill="white"
            ></path>
            <path
              d="M119.697 161.975V201.136H60.832V161.975C60.832 159.781 62.7008 157.902 64.881 157.902H115.648C118.14 157.902 119.697 159.781 119.697 161.975Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M90.4219 192.677C97.5853 192.677 103.814 186.725 103.814 179.519C103.814 172.313 97.8968 166.361 90.4219 166.361C83.2584 166.361 77.0293 172.313 77.0293 179.519C77.3408 186.725 82.9469 192.677 90.4219 192.677Z"
              fill="#111827"
            ></path>
            <path
              d="M110.043 200.509V200.823H70.4883V200.509C70.4883 199.57 71.1112 198.943 72.0456 198.943H108.174C109.42 198.943 110.043 199.57 110.043 200.509Z"
              fill="#111827"
            ></path>
            <path
              d="M188.53 161.975V201.136H129.665V161.975C129.665 159.781 131.533 157.902 133.713 157.902H184.169C186.661 157.902 188.53 159.781 188.53 161.975Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M158.941 192.677C166.105 192.677 172.334 186.725 172.334 179.519C172.334 172.313 166.416 166.361 158.941 166.361C151.778 166.361 145.549 172.313 145.549 179.519C145.549 186.725 151.778 192.677 158.941 192.677Z"
              fill="white"
            ></path>
            <path
              d="M178.876 200.509V200.823H139.321V200.509C139.321 199.57 139.944 198.943 141.19 198.943H177.318C178.253 198.943 178.876 199.57 178.876 200.509Z"
              fill="white"
            ></path>
            <path
              d="M96.7582 223H13.2418C6.4408 223 1 217.561 1 210.762V108.238C1 101.439 6.4408 96 13.2418 96H96.7582C103.559 96 109 101.439 109 108.238V210.762C109 217.561 103.831 223 96.7582 223Z"
              fill="white"
            ></path>
            <path
              d="M1 134.617V108.238C1 101.439 6.4408 96 13.2418 96H96.7582C103.559 96 109 101.439 109 108.238V210.762C109 217.561 103.831 223 96.7582 223H69.2821"
              stroke="#333C4C"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M81.7357 190H17.2643C15.6321 190 14 188.8 14 187C14 185.2 15.0881 184 17.2643 184H81.7357C83.3679 184 85 185.2 85 187C84.728 188.8 83.3679 190 81.7357 190Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M42.5042 206H17.2268C15.6134 206 14 204.8 14 203C14 201.2 15.0756 200 17.2268 200H42.7731C44.3865 200 46 201.2 46 203C45.4622 204.5 44.1177 206 42.5042 206Z"
              fill="#F5F5F5"
            ></path>
            <path
              d="M92.7272 169H18.2728C16.3637 169 15 167.351 15 165.701V114.299C15 112.374 16.6364 111 18.2728 111H92.7272C94.6363 111 96 112.649 96 114.299V165.701C96 167.9 94.3636 169 92.7272 169Z"
              fill="#F5F5F5"
            ></path>
            <path d="M57 157H30L43.5 141L57 157Z" fill="#22C55E"></path>
            <path d="M79 157H41L59.8652 135L79 157Z" fill="white"></path>
            <path
              d="M40.5 136C43.3947 136 46 133.895 46 131C46 128.368 43.6842 126 40.5 126C37.3158 126 35 128.105 35 131C35 133.632 37.3158 136 40.5 136Z"
              fill="#22C55E"
            ></path>
            <path
              d="M88.5426 205.831C88.3493 206.025 88.3493 206.22 87.9627 206.414L84.2897 207.97C83.7098 208.164 83.1299 208.553 82.7432 208.942C82.3566 209.331 81.97 209.914 81.97 210.303L80.4235 213.998C80.2302 214.581 79.6502 214.97 79.0703 214.581C78.877 214.387 78.6837 214.387 78.6837 214.192L71.3379 198.053C71.1445 197.858 71.1445 197.469 71.3379 197.275C71.5312 197.081 71.9178 196.886 72.3044 197.081L88.3493 204.47C88.5426 204.664 88.7359 205.247 88.5426 205.831Z"
              fill="#111827"
            ></path>
            <path
              d="M91.4429 215.748C91.2496 215.748 91.2496 215.748 91.0563 215.553L85.8369 210.109C85.6436 209.914 85.6436 209.331 85.8369 209.331C86.0302 209.136 86.6101 209.136 86.6101 209.331L91.8295 214.581C92.0229 214.776 92.0229 215.359 91.8295 215.359C92.0229 215.748 91.6362 215.748 91.4429 215.748Z"
              fill="#111827"
            ></path>
            <path
              d="M86.2237 215.748C86.0304 215.748 86.0304 215.748 85.837 215.553L83.3237 213.025C83.1304 212.831 83.1304 212.248 83.3237 212.248C83.517 212.053 84.097 212.053 84.097 212.248L86.6104 214.776C86.8037 214.97 86.8037 215.553 86.6104 215.748C86.6104 215.748 86.417 215.748 86.2237 215.748Z"
              fill="#111827"
            ></path>
            <path
              d="M82.5503 208.942C82.1637 209.331 81.7771 209.914 81.7771 210.303L80.2306 213.998C80.0373 214.581 79.4574 214.97 78.8774 214.581C78.6841 214.387 78.4908 214.387 78.4908 214.192L71.145 198.053C70.9517 197.858 70.9517 197.469 71.145 197.275L82.5503 208.942Z"
              fill="#111827"
            ></path>
            <path
              d="M282.59 248.443H207.529C203.169 248.443 199.12 244.684 199.12 239.984V133.779C199.12 129.392 202.858 125.32 207.529 125.32H282.59C286.951 125.32 291 129.079 291 133.779V239.984C291 244.684 287.574 248.443 282.59 248.443Z"
              fill="white"
            ></path>
            <path
              d="M198.808 153.516V133.779C198.808 129.392 202.545 125.32 207.217 125.32H282.278C286.638 125.32 290.687 129.079 290.687 133.779V239.984C290.687 244.684 287.261 248.443 282.278 248.443H207.217C202.857 248.443 198.808 244.684 198.808 239.984V174.82"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M257.362 182.025C253.936 184.218 249.575 185.785 244.903 185.785C232.445 185.785 222.79 175.76 222.79 163.541C222.79 151.01 232.757 141.297 244.903 141.297C257.362 141.297 267.017 151.323 267.017 163.541C267.64 171.373 263.591 177.953 257.362 182.025Z"
              fill="#FBBF24"
            ></path>
            <path
              d="M247.394 141.297C258.607 142.551 267.327 151.949 267.327 163.541C267.639 171.373 263.901 177.953 257.672 182.025C254.246 184.218 249.886 185.785 245.214 185.785C232.756 185.785 223.101 175.76 223.101 163.541"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M229.329 147.563C231.51 145.37 234.313 143.49 237.116 142.551"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M243.656 172L237.427 165.734C236.804 165.108 236.804 163.541 237.427 162.915C238.05 162.288 239.607 162.288 240.23 162.915L243.033 165.734V155.396C243.033 154.456 243.968 153.203 245.214 153.203C246.148 153.203 247.394 154.142 247.394 155.396V165.421L250.197 162.601C250.82 161.975 252.377 161.975 253 162.601C253.311 162.915 253.311 163.541 253.311 163.854C253.311 164.168 253 164.794 253 165.108L246.771 171.373C246.459 171.687 245.836 171.687 245.525 171.687C244.591 172.313 243.968 172.313 243.656 172Z"
              fill="white"
            ></path>
            <path
              d="M275.736 207.089L270.753 234.032C270.441 236.225 268.261 238.104 266.081 238.104H224.657C222.165 238.104 220.297 236.225 219.985 234.032L215.002 207.089"
              fill="white"
            ></path>
            <path
              d="M275.736 207.089L275.424 209.595L270.753 234.032C270.441 236.225 268.261 238.104 266.081 238.104H224.657C222.165 238.104 220.297 236.225 219.985 234.032L215.313 209.595L215.002 207.089H275.736Z"
              fill="#22C55E"
            ></path>
            <path
              d="M275.738 207.089L275.115 210.848H215.315L214.692 207.089H275.738Z"
              fill="#16A34A"
            ></path>
            <path
              d="M216.56 207.089H215.003C213.134 207.089 211.577 205.522 211.577 203.956V200.823C211.577 199.256 213.134 197.69 215.003 197.69H275.737C277.606 197.69 279.163 198.943 279.163 200.823V203.956C279.163 205.522 277.606 207.089 275.737 207.089H274.18"
              fill="#22C55E"
            ></path>
            <path
              d="M227.15 213.981C226.839 213.354 226.216 212.728 225.593 212.728C224.97 213.041 224.347 213.668 224.347 214.294L227.773 230.585C228.085 232.152 230.888 231.839 230.265 230.272L227.15 213.981Z"
              fill="#16A34A"
            ></path>
            <path
              d="M236.182 213.981C236.182 213.354 235.559 212.728 234.625 212.728C234.002 212.728 233.379 213.354 233.379 214.294C233.69 217.74 235.248 227.139 235.871 230.585C236.182 232.465 238.674 231.839 238.363 230.272C238.051 228.706 236.494 216.174 236.182 213.981Z"
              fill="#16A34A"
            ></path>
            <path
              d="M243.967 214.294V230.585C243.967 232.465 246.77 232.465 246.77 230.585V214.294C246.459 212.415 243.967 212.415 243.967 214.294Z"
              fill="#16A34A"
            ></path>
            <path
              d="M253.937 213.981C252.691 222.44 253.314 218.367 251.445 230.272C251.134 232.152 253.937 232.465 253.937 230.585L255.183 221.187L256.117 214.921C256.74 212.728 254.248 212.414 253.937 213.981Z"
              fill="#16A34A"
            ></path>
            <path
              d="M263.28 213.981C262.968 216.174 260.165 229.332 259.853 230.272C259.542 231.839 262.345 232.465 262.345 230.586L265.772 214.294C265.772 212.728 263.28 212.415 263.28 213.981Z"
              fill="#16A34A"
            ></path>
            <path
              d="M232.492 24.3861C233.738 24.3861 234.672 23.4462 234.672 22.193C234.672 20.9399 233.738 20 232.492 20C231.246 20 230 20.9399 230 22.193C230 23.4462 230.934 24.3861 232.492 24.3861Z"
              fill="#22C55E"
            ></path>
            <path
              d="M33.1804 255.386C34.4264 255.386 35.3609 254.446 35.3609 253.193C35.3609 251.94 34.4264 251 33.1804 251C31.9345 251 31 251.94 31 253.193C31 254.446 31.9345 255.386 33.1804 255.386Z"
              fill="#22C55E"
            ></path>
            <path
              d="M220.607 197.69H214.69C212.821 197.69 211.264 199.256 211.264 200.823V203.956C211.264 205.522 212.821 207.089 214.69 207.089L215.001 209.595L219.673 234.032C219.984 236.538 222.165 238.104 224.345 238.104H265.768C267.949 238.104 269.817 236.225 270.44 234.032L275.112 209.595L275.424 207.089H275.112C276.981 207.089 278.538 205.522 278.538 203.956V200.823C278.538 199.256 276.981 197.69 275.112 197.69H237.115"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M115.027 201.136H183.236"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M30.311 271H291"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
            <path
              d="M11.6245 271H21.2796"
              stroke="#333C4C"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>

          <span className="text-2xl text-gray-200 font-bold">
            لطفا ابتدا دوره را خریداری کنید
          </span>
          <p className="text-lg text-gray-300">
            برای دیدن جلسه دوره مد نظر، لطفا ابتدا دوره را خریداری کنید.
          </p>

          <Link
            to={"/shop"}
            className="flex justify-center items-center py-3 px-10 rounded-xl bg-gradient-to-tl from-green-700 to-green-400 text-white hover:opacity-80 transition-all duration-300"
          >
            فروشگاه
          </Link>
        </div>
      ) : (
        !token ||
        (!user?.user?.name && (
          <div className="w-full bg-[#222] max-w-[1300px] rounded-2xl p-10 flex flex-col gap-5  justify-center items-center">
            <svg
              class="w-80"
              viewBox="0 0 292 272"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="146" cy="142" r="130" fill="#64748B"></circle>
              <path
                d="M82.7 5.2H78.8V1.3C78.8 0.65 78.15 0 77.5 0C76.85 0 76.2 0.65 76.2 1.3V5.2H72.3C71.65 5.2 71 5.85 71 6.5C71 7.15 71.65 7.8 72.3 7.8H76.2V11.7C76.2 12.35 76.85 13 77.5 13C78.15 13 78.8 12.35 78.8 11.7V7.8H82.7C83.35 7.8 84 7.15 84 6.5C84 5.85 83.35 5.2 82.7 5.2Z"
                fill="#FBBF24"
              ></path>
              <path
                d="M144.5 50C146.433 50 148 48.433 148 46.5C148 44.567 146.433 43 144.5 43C142.567 43 141 44.567 141 46.5C141 48.433 142.567 50 144.5 50Z"
                fill="#FBBF24"
              ></path>
              <path
                d="M200.054 270.687C182.924 271 162.056 270.687 147.418 270.373C133.091 270.687 112.223 271 94.7817 270.687C111.912 260.975 117.518 242.804 118.452 230.899H175.76C177.318 242.804 182.924 261.288 200.054 270.687Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M95.0928 270.687C112.223 260.975 117.829 242.804 118.763 230.899H176.071C177.006 239.044 179.809 250.323 187.284 259.721"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M255.492 79.2658V229.019C255.492 232.779 252.377 235.285 249.263 235.285H46.8165C43.3905 235.285 40.5874 232.152 40.5874 229.019V79.2658C40.5874 75.8196 43.702 73 46.8165 73H249.263C253 73 255.492 76.1329 255.492 79.2658Z"
                fill="white"
              ></path>
              <path
                d="M255.492 128.139V229.019C255.492 232.779 252.377 235.285 249.263 235.285H46.8165C43.3905 235.285 40.5874 232.152 40.5874 229.019"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M40.2764 212.101V79.2658C40.2764 75.8196 43.3909 73 46.5055 73H248.952C252.689 73 255.181 76.1329 255.181 79.2658V111.535"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M247.396 81.1455H47.7524V200.823H247.396V81.1455Z"
                fill="white"
              ></path>
              <path
                d="M147.417 222.753C150.22 222.753 152.4 220.56 152.4 217.74C152.4 214.921 149.909 212.728 147.417 212.728C144.925 212.728 142.434 214.921 142.434 217.74C142.434 220.247 144.614 222.753 147.417 222.753Z"
                fill="white"
              ></path>
              <path
                d="M236.183 194.557H205.349C202.857 194.557 200.677 192.364 200.677 189.858V91.7976C200.677 89.2913 202.857 87.0983 205.349 87.0983H236.183C238.674 87.0983 240.855 89.2913 240.855 91.7976V189.858C240.855 192.364 238.986 194.557 236.183 194.557Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M231.198 94.6171H210.642C210.019 94.6171 209.396 93.9905 209.396 93.3639C209.396 92.7373 210.019 92.1107 210.642 92.1107H231.198C231.821 92.1107 232.444 92.7373 232.444 93.3639C232.444 93.9905 231.821 94.6171 231.198 94.6171Z"
                fill="white"
              ></path>
              <path
                d="M234.626 104.956H207.217C206.595 104.956 205.972 104.642 205.972 103.702C205.972 103.076 206.283 102.449 207.217 102.449H234.626C235.249 102.449 235.871 102.763 235.871 103.702C235.871 104.642 235.249 104.956 234.626 104.956Z"
                fill="white"
              ></path>
              <path
                d="M234.626 111.535H207.217C206.595 111.535 205.972 111.222 205.972 110.282C205.972 109.655 206.283 109.029 207.217 109.029H234.626C235.249 109.029 235.871 109.342 235.871 110.282C235.871 111.222 235.249 111.535 234.626 111.535Z"
                fill="white"
              ></path>
              <path
                d="M234.626 117.801H207.217C206.595 117.801 205.972 117.487 205.972 116.547C205.972 115.921 206.283 115.294 207.217 115.294H234.626C235.249 115.294 235.871 115.607 235.871 116.547C235.871 117.487 235.249 117.801 234.626 117.801Z"
                fill="white"
              ></path>
              <path
                d="M234.626 124.38H207.217C206.595 124.38 205.972 124.066 205.972 123.126C205.972 122.5 206.283 121.873 207.217 121.873H234.626C235.249 121.873 235.871 122.187 235.871 123.126C235.871 123.753 235.249 124.38 234.626 124.38Z"
                fill="white"
              ></path>
              <path
                d="M234.626 130.646H207.217C206.595 130.646 205.972 130.332 205.972 129.392C205.972 128.766 206.283 128.139 207.217 128.139H234.626C235.249 128.139 235.871 128.453 235.871 129.392C235.871 130.019 235.249 130.646 234.626 130.646Z"
                fill="#111827"
              ></path>
              <path
                d="M234.626 137.225H207.217C206.595 137.225 205.972 136.911 205.972 135.971C205.972 135.345 206.283 134.718 207.217 134.718H234.626C235.249 134.718 235.871 135.032 235.871 135.971C235.871 136.598 235.249 137.225 234.626 137.225Z"
                fill="#111827"
              ></path>
              <path
                d="M234.626 143.49H207.217C206.595 143.49 205.972 143.177 205.972 142.237C205.972 141.297 206.283 140.984 207.217 140.984H234.626C235.249 140.984 235.871 141.297 235.871 142.237C235.871 142.864 235.249 143.49 234.626 143.49Z"
                fill="#111827"
              ></path>
              <path
                d="M234.626 149.756H207.217C206.595 149.756 205.972 149.443 205.972 148.503C205.972 147.563 206.283 147.25 207.217 147.25H234.626C235.249 147.25 235.871 147.563 235.871 148.503C235.871 149.13 235.249 149.756 234.626 149.756Z"
                fill="#111827"
              ></path>
              <path
                d="M234.626 156.335H207.217C206.595 156.335 205.972 156.022 205.972 155.082C205.972 154.142 206.283 153.829 207.217 153.829H234.626C235.249 153.829 235.871 154.142 235.871 155.082C235.871 155.709 235.249 156.335 234.626 156.335Z"
                fill="#111827"
              ></path>
              <path
                d="M65.1924 149.756H115.648C117.828 149.756 119.697 147.876 119.697 145.683V93.3638C119.697 91.1708 117.828 89.291 115.648 89.291H64.881C62.7008 89.291 60.832 91.1708 60.832 93.3638V145.997C61.1435 147.876 63.0123 149.756 65.1924 149.756Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M90.4219 124.066C97.5853 124.066 103.814 118.114 103.814 110.908C103.814 103.703 97.8968 97.75 90.4219 97.75C83.2584 97.75 77.0293 103.703 77.0293 110.908C77.0293 118.114 82.9469 124.066 90.4219 124.066Z"
                fill="#111827"
              ></path>
              <path
                d="M108.797 133.465H72.6681C71.7338 133.465 71.1108 132.839 71.1108 131.899C71.1108 130.959 71.7338 130.332 72.6681 130.332H108.797C109.731 130.332 110.354 130.959 110.354 131.899C110.043 132.839 109.42 133.465 108.797 133.465Z"
                fill="#111827"
              ></path>
              <path
                d="M97.8955 140.671H83.5685C82.6341 140.671 82.0112 140.044 82.0112 139.104C82.0112 138.165 82.6341 137.538 83.5685 137.538H97.8955C98.8299 137.538 99.4528 138.165 99.4528 139.104C99.4528 139.731 98.8299 140.671 97.8955 140.671Z"
                fill="#111827"
              ></path>
              <path
                d="M133.401 149.756H184.169C186.349 149.756 188.218 147.876 188.218 145.683V93.3638C188.218 91.1708 186.349 89.291 184.169 89.291H133.401C131.221 89.291 129.353 91.1708 129.353 93.3638V145.997C129.664 147.876 131.533 149.756 133.401 149.756Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M158.941 124.066C166.105 124.066 172.334 118.114 172.334 110.908C172.334 103.703 166.416 97.75 158.941 97.75C151.778 97.75 145.549 103.703 145.549 110.908C145.549 118.114 151.778 124.066 158.941 124.066Z"
                fill="white"
              ></path>
              <path
                d="M177.318 133.465H140.878C139.944 133.465 139.321 132.839 139.321 131.899C139.321 130.959 139.944 130.332 140.878 130.332H177.007C177.941 130.332 178.564 130.959 178.564 131.899C178.876 132.839 178.253 133.465 177.318 133.465Z"
                fill="white"
              ></path>
              <path
                d="M166.105 140.671H151.778C150.844 140.671 150.221 140.044 150.221 139.104C150.221 138.165 150.844 137.538 151.778 137.538H166.105C167.039 137.538 167.662 138.165 167.662 139.104C167.974 139.731 167.039 140.671 166.105 140.671Z"
                fill="white"
              ></path>
              <path
                d="M119.697 161.975V201.136H60.832V161.975C60.832 159.781 62.7008 157.902 64.881 157.902H115.648C118.14 157.902 119.697 159.781 119.697 161.975Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M90.4219 192.677C97.5853 192.677 103.814 186.725 103.814 179.519C103.814 172.313 97.8968 166.361 90.4219 166.361C83.2584 166.361 77.0293 172.313 77.0293 179.519C77.3408 186.725 82.9469 192.677 90.4219 192.677Z"
                fill="#111827"
              ></path>
              <path
                d="M110.043 200.509V200.823H70.4883V200.509C70.4883 199.57 71.1112 198.943 72.0456 198.943H108.174C109.42 198.943 110.043 199.57 110.043 200.509Z"
                fill="#111827"
              ></path>
              <path
                d="M188.53 161.975V201.136H129.665V161.975C129.665 159.781 131.533 157.902 133.713 157.902H184.169C186.661 157.902 188.53 159.781 188.53 161.975Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M158.941 192.677C166.105 192.677 172.334 186.725 172.334 179.519C172.334 172.313 166.416 166.361 158.941 166.361C151.778 166.361 145.549 172.313 145.549 179.519C145.549 186.725 151.778 192.677 158.941 192.677Z"
                fill="white"
              ></path>
              <path
                d="M178.876 200.509V200.823H139.321V200.509C139.321 199.57 139.944 198.943 141.19 198.943H177.318C178.253 198.943 178.876 199.57 178.876 200.509Z"
                fill="white"
              ></path>
              <path
                d="M96.7582 223H13.2418C6.4408 223 1 217.561 1 210.762V108.238C1 101.439 6.4408 96 13.2418 96H96.7582C103.559 96 109 101.439 109 108.238V210.762C109 217.561 103.831 223 96.7582 223Z"
                fill="white"
              ></path>
              <path
                d="M1 134.617V108.238C1 101.439 6.4408 96 13.2418 96H96.7582C103.559 96 109 101.439 109 108.238V210.762C109 217.561 103.831 223 96.7582 223H69.2821"
                stroke="#333C4C"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M81.7357 190H17.2643C15.6321 190 14 188.8 14 187C14 185.2 15.0881 184 17.2643 184H81.7357C83.3679 184 85 185.2 85 187C84.728 188.8 83.3679 190 81.7357 190Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M42.5042 206H17.2268C15.6134 206 14 204.8 14 203C14 201.2 15.0756 200 17.2268 200H42.7731C44.3865 200 46 201.2 46 203C45.4622 204.5 44.1177 206 42.5042 206Z"
                fill="#F5F5F5"
              ></path>
              <path
                d="M92.7272 169H18.2728C16.3637 169 15 167.351 15 165.701V114.299C15 112.374 16.6364 111 18.2728 111H92.7272C94.6363 111 96 112.649 96 114.299V165.701C96 167.9 94.3636 169 92.7272 169Z"
                fill="#F5F5F5"
              ></path>
              <path d="M57 157H30L43.5 141L57 157Z" fill="#22C55E"></path>
              <path d="M79 157H41L59.8652 135L79 157Z" fill="white"></path>
              <path
                d="M40.5 136C43.3947 136 46 133.895 46 131C46 128.368 43.6842 126 40.5 126C37.3158 126 35 128.105 35 131C35 133.632 37.3158 136 40.5 136Z"
                fill="#22C55E"
              ></path>
              <path
                d="M88.5426 205.831C88.3493 206.025 88.3493 206.22 87.9627 206.414L84.2897 207.97C83.7098 208.164 83.1299 208.553 82.7432 208.942C82.3566 209.331 81.97 209.914 81.97 210.303L80.4235 213.998C80.2302 214.581 79.6502 214.97 79.0703 214.581C78.877 214.387 78.6837 214.387 78.6837 214.192L71.3379 198.053C71.1445 197.858 71.1445 197.469 71.3379 197.275C71.5312 197.081 71.9178 196.886 72.3044 197.081L88.3493 204.47C88.5426 204.664 88.7359 205.247 88.5426 205.831Z"
                fill="#111827"
              ></path>
              <path
                d="M91.4429 215.748C91.2496 215.748 91.2496 215.748 91.0563 215.553L85.8369 210.109C85.6436 209.914 85.6436 209.331 85.8369 209.331C86.0302 209.136 86.6101 209.136 86.6101 209.331L91.8295 214.581C92.0229 214.776 92.0229 215.359 91.8295 215.359C92.0229 215.748 91.6362 215.748 91.4429 215.748Z"
                fill="#111827"
              ></path>
              <path
                d="M86.2237 215.748C86.0304 215.748 86.0304 215.748 85.837 215.553L83.3237 213.025C83.1304 212.831 83.1304 212.248 83.3237 212.248C83.517 212.053 84.097 212.053 84.097 212.248L86.6104 214.776C86.8037 214.97 86.8037 215.553 86.6104 215.748C86.6104 215.748 86.417 215.748 86.2237 215.748Z"
                fill="#111827"
              ></path>
              <path
                d="M82.5503 208.942C82.1637 209.331 81.7771 209.914 81.7771 210.303L80.2306 213.998C80.0373 214.581 79.4574 214.97 78.8774 214.581C78.6841 214.387 78.4908 214.387 78.4908 214.192L71.145 198.053C70.9517 197.858 70.9517 197.469 71.145 197.275L82.5503 208.942Z"
                fill="#111827"
              ></path>
              <path
                d="M282.59 248.443H207.529C203.169 248.443 199.12 244.684 199.12 239.984V133.779C199.12 129.392 202.858 125.32 207.529 125.32H282.59C286.951 125.32 291 129.079 291 133.779V239.984C291 244.684 287.574 248.443 282.59 248.443Z"
                fill="white"
              ></path>
              <path
                d="M198.808 153.516V133.779C198.808 129.392 202.545 125.32 207.217 125.32H282.278C286.638 125.32 290.687 129.079 290.687 133.779V239.984C290.687 244.684 287.261 248.443 282.278 248.443H207.217C202.857 248.443 198.808 244.684 198.808 239.984V174.82"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M257.362 182.025C253.936 184.218 249.575 185.785 244.903 185.785C232.445 185.785 222.79 175.76 222.79 163.541C222.79 151.01 232.757 141.297 244.903 141.297C257.362 141.297 267.017 151.323 267.017 163.541C267.64 171.373 263.591 177.953 257.362 182.025Z"
                fill="#FBBF24"
              ></path>
              <path
                d="M247.394 141.297C258.607 142.551 267.327 151.949 267.327 163.541C267.639 171.373 263.901 177.953 257.672 182.025C254.246 184.218 249.886 185.785 245.214 185.785C232.756 185.785 223.101 175.76 223.101 163.541"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M229.329 147.563C231.51 145.37 234.313 143.49 237.116 142.551"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M243.656 172L237.427 165.734C236.804 165.108 236.804 163.541 237.427 162.915C238.05 162.288 239.607 162.288 240.23 162.915L243.033 165.734V155.396C243.033 154.456 243.968 153.203 245.214 153.203C246.148 153.203 247.394 154.142 247.394 155.396V165.421L250.197 162.601C250.82 161.975 252.377 161.975 253 162.601C253.311 162.915 253.311 163.541 253.311 163.854C253.311 164.168 253 164.794 253 165.108L246.771 171.373C246.459 171.687 245.836 171.687 245.525 171.687C244.591 172.313 243.968 172.313 243.656 172Z"
                fill="white"
              ></path>
              <path
                d="M275.736 207.089L270.753 234.032C270.441 236.225 268.261 238.104 266.081 238.104H224.657C222.165 238.104 220.297 236.225 219.985 234.032L215.002 207.089"
                fill="white"
              ></path>
              <path
                d="M275.736 207.089L275.424 209.595L270.753 234.032C270.441 236.225 268.261 238.104 266.081 238.104H224.657C222.165 238.104 220.297 236.225 219.985 234.032L215.313 209.595L215.002 207.089H275.736Z"
                fill="#22C55E"
              ></path>
              <path
                d="M275.738 207.089L275.115 210.848H215.315L214.692 207.089H275.738Z"
                fill="#16A34A"
              ></path>
              <path
                d="M216.56 207.089H215.003C213.134 207.089 211.577 205.522 211.577 203.956V200.823C211.577 199.256 213.134 197.69 215.003 197.69H275.737C277.606 197.69 279.163 198.943 279.163 200.823V203.956C279.163 205.522 277.606 207.089 275.737 207.089H274.18"
                fill="#22C55E"
              ></path>
              <path
                d="M227.15 213.981C226.839 213.354 226.216 212.728 225.593 212.728C224.97 213.041 224.347 213.668 224.347 214.294L227.773 230.585C228.085 232.152 230.888 231.839 230.265 230.272L227.15 213.981Z"
                fill="#16A34A"
              ></path>
              <path
                d="M236.182 213.981C236.182 213.354 235.559 212.728 234.625 212.728C234.002 212.728 233.379 213.354 233.379 214.294C233.69 217.74 235.248 227.139 235.871 230.585C236.182 232.465 238.674 231.839 238.363 230.272C238.051 228.706 236.494 216.174 236.182 213.981Z"
                fill="#16A34A"
              ></path>
              <path
                d="M243.967 214.294V230.585C243.967 232.465 246.77 232.465 246.77 230.585V214.294C246.459 212.415 243.967 212.415 243.967 214.294Z"
                fill="#16A34A"
              ></path>
              <path
                d="M253.937 213.981C252.691 222.44 253.314 218.367 251.445 230.272C251.134 232.152 253.937 232.465 253.937 230.585L255.183 221.187L256.117 214.921C256.74 212.728 254.248 212.414 253.937 213.981Z"
                fill="#16A34A"
              ></path>
              <path
                d="M263.28 213.981C262.968 216.174 260.165 229.332 259.853 230.272C259.542 231.839 262.345 232.465 262.345 230.586L265.772 214.294C265.772 212.728 263.28 212.415 263.28 213.981Z"
                fill="#16A34A"
              ></path>
              <path
                d="M232.492 24.3861C233.738 24.3861 234.672 23.4462 234.672 22.193C234.672 20.9399 233.738 20 232.492 20C231.246 20 230 20.9399 230 22.193C230 23.4462 230.934 24.3861 232.492 24.3861Z"
                fill="#22C55E"
              ></path>
              <path
                d="M33.1804 255.386C34.4264 255.386 35.3609 254.446 35.3609 253.193C35.3609 251.94 34.4264 251 33.1804 251C31.9345 251 31 251.94 31 253.193C31 254.446 31.9345 255.386 33.1804 255.386Z"
                fill="#22C55E"
              ></path>
              <path
                d="M220.607 197.69H214.69C212.821 197.69 211.264 199.256 211.264 200.823V203.956C211.264 205.522 212.821 207.089 214.69 207.089L215.001 209.595L219.673 234.032C219.984 236.538 222.165 238.104 224.345 238.104H265.768C267.949 238.104 269.817 236.225 270.44 234.032L275.112 209.595L275.424 207.089H275.112C276.981 207.089 278.538 205.522 278.538 203.956V200.823C278.538 199.256 276.981 197.69 275.112 197.69H237.115"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M115.027 201.136H183.236"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M30.311 271H291"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <path
                d="M11.6245 271H21.2796"
                stroke="#333C4C"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>

            <span className="text-2xl text-gray-200 font-bold">
              لطفا ابتدا وارد شوید
            </span>
            <p className="text-lg text-gray-300">
              برای دیدن جلسه دوره مد نظر، لطفا ابتدا با آن حسابی که دوره را تهیه
              کرده‌اید وارد سایت شوید.
            </p>

            <Link
              to={"/auth"}
              className="flex justify-center items-center py-3 px-10 rounded-xl bg-gradient-to-tl from-green-700 to-green-400 text-white hover:opacity-80 transition-all duration-300"
            >
              ورود
            </Link>
          </div>
        ))
      )}

      <Footer />
    </div>
  );
};

export default EpisodePage;
